<template>
  <b-modal
    id="modal-user-action"
    class="mc-modal mc-user-modal"
    :title="getModalTitle"
    size="lg"
    centered
    scrollable
    footer-class="d-flex justify-content-between"
    @hidden="resetModal"
    @ok="handleOk"
  >
    <div class="card-body ps-2">
      <form @submit.stop.prevent="handleSubmit">
        <!-- User Information Header -->
        <UserInfoHeader v-if="actionType !== 'edituser'" :user-data="userData" />

        <!-- Dynamic Content Based on Action Type -->
        <component
          :is="currentComponent"
          v-bind="componentProps"
          @update-form="updateForm"
          @field-change="handleFieldChange"
        />
      </form>
    </div>

    <template #modal-footer="{ cancel }">
      <div></div>
      <div>
        <McButton
          variant="secondary"
          class="me-3"
          :text="$t('global.action.cancel')"
          @click="cancel()"
        />
        <McButton
          variant="primary"
          :disabled="isSubmitDisabled"
          :text="getSubmitButtonText"
          @click="handleOk"
        />
      </div>
    </template>
  </b-modal>
</template>

<script>
import UserInfoHeader from './UserInfoHeader.vue'
import SetVerificationForm from './form/SetVerificationForm.vue'
import SetPasswordForm from './form/SetPasswordForm.vue'
import EditUserForm from './form/EditUserForm.vue'
import McButton from '@/components/McButton'
import { required, email, requiredIf, minLength, maxLength } from 'vuelidate/lib/validators'

// Custom phone number validator
const phoneNumber = (value) => {
  if (!value) return true

  const cleanPhone = value.replace(/\D/g, '')

  // Pola:
  // 1. Boleh diawali 0 lalu diikuti [1-9]...
  // 2. Atau diawali kode negara (1–3 digit) tapi setelahnya tidak boleh '0'
  const pattern = /^(0[1-9]\d{7,11}|[1-9]\d{0,2}[1-9]\d{6,10})$/

  return pattern.test(cleanPhone)
}

export default {
  name: 'UserActionModal',
  components: {
    UserInfoHeader,
    SetVerificationForm,
    SetPasswordForm,
    EditUserForm,
    McButton,
  },
  props: {
    actionType: {
      type: String,
      required: true,
      validator: (value) => ['setverification', 'setpassword', 'edituser'].includes(value),
    },
    userData: {
      type: Object,
      required: true,
    },
    initialForm: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      form: { ...this.initialForm },
      isSubmit: false,
    }
  },

  computed: {
    currentComponent() {
      const componentMap = {
        setverification: 'SetVerificationForm',
        setpassword: 'SetPasswordForm',
        edituser: 'EditUserForm',
      }
      return componentMap[this.actionType]
    },
    componentProps() {
      return {
        form: this.form,
        userData: this.userData,
        isSubmit: this.isSubmit,
      }
    },
    getModalTitle() {
      const titleMap = {
        setverification: this.$t('master.user.action.resendVerification'),
        setpassword: this.$t('master.user.setPassword.title'),
        edituser: this.$t('master.user.editUser'),
      }
      return titleMap[this.actionType]
    },
    getSubmitButtonText() {
      const buttonTextMap = {
        setverification: this.$t('master.user.action.resendVerification'),
        setpassword: this.$t('global.action.save'),
        edituser: this.$t('global.action.save'),
      }
      return buttonTextMap[this.actionType]
    },
    isSubmitDisabled() {
      if (this.isSubmit) return true

      switch (this.actionType) {
        case 'setverification':
          return !this.form.setVerification

        case 'setpassword':
          return !this.isPasswordFormValid

        case 'edituser':
          return !this.isEditFormValid

        default:
          return true
      }
    },
    isPasswordFormValid() {
      // No method selected
      if (!this.form.setVerification) {
        return false
      }

      // Email method - always valid if email exists
      if (this.form.setVerification === 'email') {
        return !!this.userData.email
      }

      // Direct method - need password validation
      if (this.form.setVerification === 'direct') {
        return (
          this.form.password &&
          this.form.repassword &&
          this.form.password === this.form.repassword &&
          this.form.password.length >= 8
        ) // Add your password requirements
      }

      return false
    },
    isEditFormValid() {
      // Disable submit button when any field is being edited
      if (this.form.isAnyFieldEditing) {
        return false
      }
      // Add your edit form validation logic here
      return true
    },
    isPasswordMatched() {
      return this.form.password === this.form.repassword && !this.$v.form.repassword.$invalid
    },
    isPasswordValid() {
      return this.isPasswordMatched && !this.$v.$invalid
    },
  },

  validations: {
    form: {
      email: {
        email,
        maxLength: maxLength(100),
      },
      phoneNo: {
        minLength: minLength(8),
        maxLength: maxLength(15),
        phoneNumber,
      },
      groups: { required },
      password: {
        required: requiredIf(function () {
          return this.$store.state.config.mode !== 'development' && this.form.isDriver
        }),
        maxLength: maxLength(50),
        lowerCase: (val) => {
          const lowercaseRegex = /[a-z]/
          return lowercaseRegex.test(val)
        },
        upperCase: (val) => {
          const uppercaseRegex = /[A-Z]/
          return uppercaseRegex.test(val)
        },
        number: (val) => {
          const numberRegex = /\d/
          return numberRegex.test(val)
        },
        specialCharacter: (val) => {
          const specialCharRegex = /[!@#$%^&*(),.?":{}|<>~`[\]\-_+=;'/\\]/
          return specialCharRegex.test(val)
        },
        minimumLength: (val) => {
          return val.length >= 8
        },
      },

      repassword: {
        required: requiredIf(function () {
          return this.$store.state.config.mode !== 'development' && this.form.isDriver
        }),
        minLength: minLength(8),
        maxLength: maxLength(50),
      },
    },
  },

  watch: {
    initialForm: {
      handler(newForm) {
        this.form = { ...newForm }
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    updateForm(updates) {
      this.form = { ...this.form, ...updates }
    },
    handleFieldChange(field, value) {
      this.$set(this.form, field, value)
    },
    handleOk(bvModalEvent) {
      // Prevent modal from closing
      bvModalEvent.preventDefault()
      this.isSubmit = true

      if (this.$v.$invalid) {
        if ((this.form.email == '' || this.form.email == null || this.$v.form.email.$invalid) && (this.form.phoneNo == '' || this.form.phoneNo == null || this.$v.form.phoneNo.$invalid)) {
          this.$mcToast.error(this.$t('master.user.emailPhoneNoEmptyError'))
          this.isSubmit = false
          return
        }

        if (!this.form.groups || this.$v.form.groups.$invalid) {
          this.$mcToast.error(this.$t('master.user.groupEmptyError'))
          this.isSubmit = false
          return
        }
      }

      if (this.form.togglePasswordDriver) {
        if (this.$v.$invalid) {
          this.$v.$touch()
          this.isSubmit = false
          return
        }
        if (this.form.password !== this.form.repassword) {
          this.differentPass = true
          this.isSubmit = false
          return
        }
        if (!this.isPasswordValid && !this.form.isCheckedPassword) {
          this.$mcToast.error(this.$t('master.user.falsePassFormat'))
          this.isSubmit = false
          return
        }
      }
      this.$emit('submit', {
        actionType: this.actionType,
        form: this.form,
      })

      // Tutup modal secara manual
      this.$bvModal.hide('modal-user-action')
      this.isSubmit = false
    },
    handleSubmit() {
      this.handleOk()
    },
    resetModal() {
      this.$emit('reset')
    },
  },
}
</script>
